import { useContext, useEffect, useMemo, useState } from 'react'
import {
  alpha,
  Box,
  Button,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { PlayArrowRounded, ReplayRounded } from '@mui/icons-material'
import RowChain, { getChangedIndex } from '../GameBoard/RowChain.jsx'
import { GameStateContext } from '../../Providers/GameStates/GameState.jsx'
import { Words } from '../../utils.jsx'

const DEMOS = {
  standard4: {
    moves: ['cane', 'cats', 'cans'],
    stages: [
      { upper: ['came'], lower: ['bats'], link: '' },
      { upper: ['came', 'cane'], lower: ['bats'], link: '' },
      { upper: ['came', 'cane'], lower: ['cats', 'bats'], link: '' },
      { upper: ['came', 'cane'], lower: ['cats', 'bats'], link: 'cans' },
    ],
  },
  standard5: {
    moves: ['bikes', 'bates', 'bites'],
    stages: [
      { upper: ['bakes'], lower: ['dates'], link: '' },
      { upper: ['bakes', 'bikes'], lower: ['dates'], link: '' },
      { upper: ['bakes', 'bikes'], lower: ['bates', 'dates'], link: '' },
      { upper: ['bakes', 'bikes'], lower: ['bates', 'dates'], link: 'bites' },
    ],
  },
}

function RulesDemo({ gameMode, onTrySample, standalone = false }) {
  const theme = useTheme()
  const { state: gameState } = useContext(GameStateContext)
  const demo = DEMOS[gameMode] || DEMOS.standard4
  const [stageIndex, setStageIndex] = useState(0)
  const [sampleGame, setSampleGame] = useState(demo.stages[0])
  const [mode, setMode] = useState(standalone ? 'interactive' : 'auto')
  const [runId, setRunId] = useState(0)
  const [guess, setGuess] = useState('')
  const [typedWord, setTypedWord] = useState('')
  const [error, setError] = useState('')
  const stage = standalone ? sampleGame : demo.stages[stageIndex]
  const isComplete = standalone
    ? Boolean(stage.link)
    : stageIndex === demo.stages.length - 1
  const wordLength = demo.stages[0].upper[0].length

  useEffect(() => {
    setStageIndex(0)
    setSampleGame(demo.stages[0])
    setMode(standalone ? 'interactive' : 'auto')
    setRunId((value) => value + 1)
    setGuess('')
    setTypedWord('')
    setError('')
  }, [demo.stages, gameMode, standalone])

  useEffect(() => {
    if (mode !== 'auto' || isComplete) return undefined
    const nextWord = demo.moves[stageIndex]
    const isWordComplete = typedWord.length === nextWord.length
    const timer = window.setTimeout(() => {
      if (isWordComplete) {
        setTypedWord('')
        setStageIndex((value) => value + 1)
      } else {
        setTypedWord(nextWord.slice(0, typedWord.length + 1))
      }
    }, 500)
    return () => window.clearTimeout(timer)
  }, [demo.moves, isComplete, mode, runId, stageIndex, typedWord])

  const matchingIndices = useMemo(() => {
    if (stage.link) return []
    return [...stage.upper[stage.upper.length - 1]].reduce(
      (indices, letter, index) =>
        letter === stage.lower[0][index] ? [...indices, index] : indices,
      [],
    )
  }, [stage])

  const completedUpperIndex = stage.link
    ? getChangedIndex(stage.upper[stage.upper.length - 1], stage.link)
    : -1
  const completedLowerIndex = stage.link
    ? getChangedIndex(stage.link, stage.lower[0])
    : -1

  const replay = () => {
    setStageIndex(0)
    setSampleGame(demo.stages[0])
    setMode('auto')
    setRunId((value) => value + 1)
    setGuess('')
    setTypedWord('')
    setError('')
  }

  const startInteractive = () => {
    setStageIndex(0)
    setSampleGame(demo.stages[0])
    setMode('interactive')
    setRunId((value) => value + 1)
    setGuess('')
    setTypedWord('')
    setError('')
  }

  const playGuess = (event) => {
    event.preventDefault()

    if (standalone) {
      const enteredWord = guess.trim().toLowerCase()
      const lengthResult = Words.validLength(enteredWord, wordLength)
      if (lengthResult?.result === 'error') {
        setError(lengthResult.errorMsg)
        return
      }

      const dictionaryResult = Words.validWord(
        enteredWord,
        gameState.validWords,
      )
      if (dictionaryResult?.result === 'error') {
        setError(dictionaryResult.errorMsg)
        return
      }

      const result = Words.checkWord(
        stage.upper[stage.upper.length - 1],
        enteredWord,
        stage.lower[0],
      )

      if (result.result === 'error') {
        setError(result.errorMsg)
        return
      }

      setSampleGame((currentGame) => {
        if (result.result === 'UPPER') {
          return {
            ...currentGame,
            upper: [...currentGame.upper, enteredWord],
          }
        }
        if (result.result === 'LOWER') {
          return {
            ...currentGame,
            lower: [enteredWord, ...currentGame.lower],
          }
        }
        return { ...currentGame, link: enteredWord }
      })
      setGuess('')
      setError('')
      return
    }

    const expectedWord = demo.moves[stageIndex]
    if (guess.trim().toLowerCase() !== expectedWord) {
      setError(`Try ${expectedWord.toUpperCase()} for this sample.`)
      return
    }
    setStageIndex((value) => value + 1)
    setGuess('')
    setError('')
  }

  const tileSx = {
    width: standalone ? { xs: 46, sm: 58 } : { xs: 39, sm: 45 },
    height: standalone ? { xs: 46, sm: 58 } : { xs: 39, sm: 45 },
    m: standalone ? '5px' : '3px',
    display: 'grid',
    placeItems: 'center',
    borderRadius: '11px',
    border: `1px solid ${theme.custom.border}`,
    fontSize: standalone
      ? { xs: '1.45rem', sm: '1.8rem' }
      : { xs: '1.25rem', sm: '1.45rem' },
    fontWeight: 900,
    textTransform: 'uppercase',
    boxShadow: '0 6px 14px rgba(15, 23, 42, 0.1)',
  }

  const entryWord = stage.link
    ? stage.link
    : mode === 'interactive'
    ? guess.toLowerCase()
    : typedWord

  const renderWord = (word, rowType, rowIndex, words) => {
    const rowCount = words.length
    const incomingIndex =
      rowIndex > 0 ? getChangedIndex(words[rowIndex - 1], word) : null
    const outgoingIndex =
      rowIndex < rowCount - 1
        ? getChangedIndex(word, words[rowIndex + 1])
        : stage.link
        ? rowType === 'upper'
          ? completedUpperIndex
          : null
        : null
    const lowerWinningIncoming =
      rowType === 'lower' && rowIndex === 0 && stage.link
        ? completedLowerIndex
        : incomingIndex
    const coloredNodeIndex =
      rowType === 'upper'
        ? rowIndex === 0
          ? outgoingIndex
          : incomingIndex
        : rowIndex < rowCount - 1
        ? outgoingIndex
        : null

    return (
      <Box
        key={word}
        className={`word-row ${
          rowType === 'upper' && rowIndex > 0
            ? 'word-row--slide-down'
            : rowType === 'lower' && rowIndex < rowCount - 1
            ? 'word-row--slide-up'
            : ''
        }`}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          py: standalone ? '7px' : '5px',
          '--step-color':
            rowType === 'upper'
              ? theme.palette.primary.main
              : theme.palette.secondary.main,
          '--chain-row-gutter': standalone ? '12px' : '8px',
          '--chain-terminal-span': standalone ? '24px' : '16px',
          '--gold-chain-length': standalone ? '30px' : '22px',
        }}
      >
        <RowChain
          wordLength={wordLength}
          incomingIndex={
            rowType === 'lower' ? lowerWinningIncoming : incomingIndex
          }
          outgoingIndex={outgoingIndex}
          hideIncomingSegment={
            rowType === 'lower' && rowIndex === 0 && Boolean(stage.link)
          }
        />
        {[...word].map((letter, letterIndex) => {
        const nearestUpper = rowType === 'upper' && rowIndex === rowCount - 1
        const nearestLower = rowType === 'lower' && rowIndex === 0
        const isGold = stage.link
          ? (nearestUpper && letterIndex === completedUpperIndex) ||
            (nearestLower && letterIndex === completedLowerIndex)
          : (nearestUpper || nearestLower) && matchingIndices.includes(letterIndex)
        const chainDirection = nearestUpper
          ? 'letter-box--chain-upper'
          : nearestLower
          ? 'letter-box--chain-lower'
          : ''

        return (
          <Box
            key={`${word}-${letterIndex}`}
            className={`letter-box ${
              letterIndex === coloredNodeIndex ? 'letter-box--step-node' : ''
            } ${
              isGold ? `letter-box--chain-node ${chainDirection}` : ''
            }`}
            sx={{
              ...tileSx,
              '--aura-color': theme.custom.aura,
              bgcolor:
                rowType === 'upper' && rowIndex === 0
                  ? theme.palette.primary.main
                  : rowType === 'lower' && rowIndex === rowCount - 1
                  ? theme.palette.secondary.main
                  : theme.custom.incorrect,
            }}
          >
            {letter}
          </Box>
        )
      })}
      </Box>
    )
  }

  return (
    <Box>
      <Box
        sx={{
          py: 1,
          minHeight: standalone
            ? wordLength === 4
              ? 390
              : 280
            : wordLength === 4
            ? 255
            : 190,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          borderRadius: '18px',
          background: `radial-gradient(circle at center, ${alpha(
            theme.custom.aura,
            0.14,
          )}, transparent 67%)`,
        }}
      >
        {stage.upper.map((word, index) =>
          renderWord(word, 'upper', index, stage.upper),
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            py: standalone ? '7px' : '5px',
            '--gold-chain-length': standalone ? '30px' : '22px',
          }}
        >
          {Array.from({ length: wordLength }, (_, index) => {
            const isGold = stage.link
              ? index === completedUpperIndex || index === completedLowerIndex
              : matchingIndices.includes(index)
            const bridgeClasses = stage.link
              ? `${
                  index === completedUpperIndex
                    ? 'letter-box--chain-bridge-upper'
                    : ''
                } ${
                  index === completedLowerIndex
                    ? 'letter-box--chain-bridge-lower'
                    : ''
                }`
              : 'letter-box--chain-bridge'
            return (
              <Box
                key={`bridge-${stageIndex}-${index}-${entryWord[index] || 'empty'}`}
                className={`letter-box ${
                  stage.link
                    ? 'letter-box--win-pop-in'
                    : entryWord[index]
                    ? 'letter-box--pop-in'
                    : ''
                } ${
                  isGold
                    ? `letter-box--chain-node ${bridgeClasses}`
                    : ''
                }`}
                sx={{
                  ...tileSx,
                  '--aura-color': theme.custom.aura,
                  bgcolor: stage.link
                    ? theme.custom.tertiary
                    : theme.palette.background.paper,
                }}
              >
                {entryWord[index] || ''}
              </Box>
            )
          })}
        </Box>
        {stage.lower.map((word, index) =>
          renderWord(word, 'lower', index, stage.lower),
        )}
      </Box>

      <Box sx={{ minHeight: 74, mt: standalone ? 2 : 1.25 }}>
        {mode === 'interactive' && !isComplete ? (
          <Box>
            {standalone && (
              <Box sx={{ textAlign: 'center', mb: 1.5 }}>
                <Typography variant="overline" sx={{ color: theme.custom.mutedText, letterSpacing: '.12em' }}>
                  Practice chain
                </Typography>
                <Typography variant="body2" sx={{ color: theme.custom.mutedText }}>
                  Build from {stage.upper[stage.upper.length - 1].toUpperCase()} or{' '}
                  {stage.lower[0].toUpperCase()}. Any valid move works.
                </Typography>
              </Box>
            )}
            <Box
              component="form"
              onSubmit={playGuess}
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'flex-start',
                maxWidth: standalone ? 470 : 'none',
                mx: 'auto',
              }}
            >
              <TextField
                autoFocus
                fullWidth
                size={standalone ? 'medium' : 'small'}
                value={guess}
                onChange={(event) => setGuess(event.target.value.slice(0, wordLength))}
                error={Boolean(error)}
                helperText={error || `Enter any valid ${wordLength}-letter word`}
                inputProps={{
                  maxLength: wordLength,
                  'aria-label': 'Sample word',
                  autoComplete: 'off',
                }}
              />
              <Button
                type="submit"
                variant="contained"
                sx={{ minWidth: standalone ? 112 : 94, minHeight: standalone ? 56 : undefined }}
              >
                Play
              </Button>
            </Box>
          </Box>
        ) : (
          <Box>
            <Typography
              variant="body2"
              sx={{ textAlign: 'center', color: theme.custom.mutedText, mb: 1 }}
            >
              {isComplete
                ? 'Chain complete — the final word connects both sides.'
                : 'Watch each valid word pull the two sides together.'}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
              <Button
                size="small"
                startIcon={<ReplayRounded />}
                onClick={standalone ? startInteractive : replay}
              >
                {standalone ? 'Restart sample' : 'Replay'}
              </Button>
              {!standalone && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PlayArrowRounded />}
                  onClick={onTrySample || startInteractive}
                >
                  Try the sample
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default RulesDemo
